import React, { useState } from "react";
import { Button, Input, Modal, Spin, Tag, Tooltip } from "antd";
import {
  FileOutlined,
  FileImageOutlined,
  PlaySquareOutlined,
  AudioOutlined,
  DeleteOutlined,
  PaperClipOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { formatToLocalDate } from "../../../../helpers/utils";
import {
  getDecryptedName,
  isCustomerLoggedIn,
} from "../../../../helpers/bycrypt";
import { svg } from "../../../../Assets/svg";
import { useAppDispatch } from "../../../../store";
import {
  createQuotationNoteAPI,
  updateQuotationNoteAPI,
  deleteQuotationNoteAPI,
} from "../quotationApis";
import { CommonConfirmationModal } from "../../../../Components/CommonConfirmationModal";
import { S3FileUploader } from "../../../../Components/S3FileUploader";
import CommonFilePreviewModal from "../../../Components/CommonFilePreviewModal";
import { deleteFromS3 } from "../../../../helpers/awsUpload";

interface NoteItem {
  _id: string;
  content: string;
  created_by?: any;
  created_at?: string;
  updated_by?: any;
  updated_at?: string;
  attachments?: string[];
}

const getFileNameFromUrl = (key: string) => {
  if (!key) return "";
  try {
    const parts = key.split("/");
    const fullName = parts[parts.length - 1];
    if (fullName.includes("_")) {
      return fullName.substring(fullName.indexOf("_") + 1);
    }
    return fullName;
  } catch (e) {
    return key;
  }
};

const getFileIcon = (fileName: string) => {
  const ext = fileName?.split(".").pop()?.toLowerCase() || "";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext)) {
    return <FileImageOutlined className="text-secondary fs-5" />;
  }
  if (["mp4", "mov", "avi", "mkv", "webm", "flv", "3gp"].includes(ext)) {
    return <PlaySquareOutlined className="text-secondary fs-5" />;
  }
  if (["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(ext)) {
    return <AudioOutlined className="text-secondary fs-5" />;
  }
  return <FileOutlined className="text-secondary fs-5" />;
};

// Helper to render attachment tags consistent with Installation Checklist
const renderAttachmentTags = (
  attachments: string[] | undefined | null,
  isEditMode: boolean,
  onRemove?: (index: number) => void,
  onPreview?: (urls: string[], index: number) => void
) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="mt-2 d-flex flex-wrap gap-2">
      {attachments.map((url, index) => {
        const fileName = getFileNameFromUrl(url);
        if (isEditMode) {
          return (
            <Tag
              key={index}
              closable
              onClose={(e) => {
                e.preventDefault();
                onRemove?.(index);
              }}
              closeIcon={
                <CloseCircleOutlined className="text-danger fs-6 ms-2" />
              }
              className="d-inline-flex align-items-center px-3 py-1 bg-white border rounded-2 fs-6 fw-semibold cursor-pointer"
              style={{
                border: "1px solid #E0E0E0",
                whiteSpace: "normal",
                wordBreak: "break-all",
                maxWidth: "100%",
                height: "auto",
              }}
            >
              <span
                onClick={() => onPreview?.(attachments, index)}
                title="Click to view file"
              >
                {fileName}
              </span>
            </Tag>
          );
        }

        return (
          <Tag
            key={index}
            className="d-inline-flex align-items-center px-3 py-1 bg-white border rounded-2 fs-6 fw-semibold cursor-pointer"
            style={{
              border: "1px solid #E0E0E0",
              whiteSpace: "normal",
              wordBreak: "break-all",
              maxWidth: "100%",
              height: "auto",
            }}
            onClick={() => onPreview?.(attachments, index)}
          >
            <span title="Click to view file">{fileName}</span>
          </Tag>
        );
      })}
    </div>
  );
};

// ── NoteRow (kept outside to prevent focus loss) ──────────────────────────────
const NoteRow = ({
  note,
  type,
  editingId,
  currentText,
  setText,
  currentAttachments,
  openUploadModal,
  removeAttachment,
  saveNote,
  cancelAction,
  handleEdit,
  onDeleteClick,
  savingState,
  onPreviewFile,
}: {
  note: NoteItem;
  type: "internal" | "client";
  editingId: string | null;
  currentText: string;
  setText: (v: string) => void;
  currentAttachments: string[];
  openUploadModal: (type: "internal" | "client") => void;
  removeAttachment: (index: number, type: "internal" | "client") => void;
  saveNote: (type: "internal" | "client") => void;
  cancelAction: (type: "internal" | "client") => void;
  handleEdit: (note: NoteItem, type: "internal" | "client") => void;
  onDeleteClick: (id: string, type: "internal" | "client") => void;
  savingState: boolean;
  onPreviewFile: (urls: string[], index: number) => void;
}) => {
  const isEditing = editingId === note._id;

  if (isEditing) {
    return (
      <div className="card border-light-subtle rounded-3 mb-2 shadow-none border-success">
        <div className="card-body py-2 px-md-3 px-2">
          <div className="d-flex align-items-center gap-3">
            <Input.TextArea
              variant="borderless"
              value={currentText}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              autoSize={{ minRows: 1, maxRows: 6 }}
              className="p-0 fs-6"
              style={{ resize: "none", textAlign: "left", direction: "ltr" }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  saveNote(type);
                }
              }}
            />
            <div className="d-flex gap-2 flex-shrink-0">
              <Tooltip title="Save">
                <Button
                  onClick={() => saveNote(type)}
                  icon={svg?.checkButton}
                  className="border-0 svg-32 p-0"
                  loading={savingState}
                />
              </Tooltip>
              <Tooltip title="Cancel">
                <Button
                  onClick={() => cancelAction(type)}
                  icon={svg?.crossButton}
                  className="border-0 svg-32 p-0"
                />
              </Tooltip>
            </div>
          </div>

          <div className="mt-2">
            <Button
              type="text"
              className="fw-bold p-0 h-auto fs-6 text-decoration-underline text-primary d-inline-flex align-items-center gap-1"
              onClick={() => openUploadModal(type)}
            >
              <PaperClipOutlined /> Add Attachment
            </Button>
          </div>

          {renderAttachmentTags(
            currentAttachments,
            true,
            (idx) => removeAttachment(idx, type),
            onPreviewFile
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card border rounded-3 mb-2 shadow-none">
      <div className="card-body py-3 px-3">
        <div className="d-flex justify-content-between gap-3">
          <p className="mb-0 fs-6 fw-semibold text-dark text-break">
            {note.content}
          </p>
          {!isCustomerLoggedIn() ? (
            <div className="d-flex gap-2 flex-shrink-0">
              <Tooltip title="Edit">
                <Button
                  icon={svg?.editBtn}
                  onClick={() => handleEdit(note, type)}
                  className="border-0 svg-32 p-0"
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  danger
                  icon={svg?.deleteBtn}
                  onClick={() => onDeleteClick(note._id, type)}
                  className="border-0 svg-32 p-0"
                />
              </Tooltip>
            </div>
          ) : (
            ""
          )}
        </div>
        <div className="d-flex flex-wrap column-gap-3 fs-7 mt-1">
          {note.created_by && (
            <small className="text-secondary">
              Created By: {getDecryptedName(note.created_by)} on{" "}
              {formatToLocalDate(note.created_at, true)}
            </small>
          )}
          {note.updated_by && note.updated_at && (
            <small className="text-secondary">
              Last Modified By: {getDecryptedName(note.updated_by)} on{" "}
              {formatToLocalDate(note.updated_at, true)}
            </small>
          )}
        </div>

        {/* View mode attachment tags */}
        {renderAttachmentTags(
          note.attachments,
          false,
          undefined,
          onPreviewFile
        )}
      </div>
    </div>
  );
};

// ── Read-only note card ───────────────────────────────────────────────────────
const ReadOnlyNoteRow = ({
  note,
  onPreviewFile,
}: {
  note: NoteItem;
  onPreviewFile: (urls: string[], index: number) => void;
}) => (
  <div key={note._id} className="card border rounded-3 mb-2 shadow-none">
    <div className="card-body py-3 px-3">
      <p className="mb-0 fs-6 fw-semibold text-dark text-break">
        {note.content}
      </p>
      <div className="d-flex flex-wrap column-gap-3 fs-7 mt-1">
        {note.created_by && (
          <small className="text-secondary">
            Created By: {getDecryptedName(note.created_by)} on{" "}
            {formatToLocalDate(note.created_at, true)}
          </small>
        )}
        {note.updated_by && note.updated_at && (
          <small className="text-secondary">
            Last Modified By: {getDecryptedName(note.updated_by)} on{" "}
            {formatToLocalDate(note.updated_at, true)}
          </small>
        )}
      </div>

      {renderAttachmentTags(
        note.attachments,
        false,
        undefined,
        onPreviewFile
      )}
    </div>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const NotesDetails = ({
  data,
  quoteId,
  onNotesChange,
}: {
  data: any;
  quoteId?: string; // ✅ optional — if not provided, renders read-only
  onNotesChange?: (notes: any) => void;
}) => {
  const dispatch = useAppDispatch();

  // ✅ order notes take priority, fallback to quote notes
  const resolvedNotes = data?.order?.notes || data?.notes;

  const mapBackendNotes = (notesList: any[] = []) =>
    notesList.map((n) => ({
      ...n,
      attachments: (n.attachments || [])
        .map((a: any) => (typeof a === "string" ? a : a?.file_url || ""))
        .filter(Boolean),
    }));

  const [internalNotes, setInternalNotes] = useState<NoteItem[]>(
    mapBackendNotes(resolvedNotes?.internal || []),
  );
  const [clientNotes, setClientNotes] = useState<NoteItem[]>(
    mapBackendNotes(resolvedNotes?.client || []),
  );

  const [isAddingInternal, setIsAddingInternal] = useState(false);
  const [isAddingClient, setIsAddingClient] = useState(false);

  const [editingInternalId, setEditingInternalId] = useState<string | null>(
    null,
  );
  const [editingClientId, setEditingClientId] = useState<string | null>(null);

  const [internalText, setInternalText] = useState("");
  const [clientText, setClientText] = useState("");

  const [internalAttachments, setInternalAttachments] = useState<string[]>([]);
  const [clientAttachments, setClientAttachments] = useState<string[]>([]);

  const [savingInternal, setSavingInternal] = useState(false);
  const [savingClient, setSavingClient] = useState(false);

  const [noteIdToDelete, setNoteIdToDelete] = useState<string | null>(null);
  const [noteTypeToDelete, setNoteTypeToDelete] = useState<
    "internal" | "client" | null
  >(null);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadTargetType, setUploadTargetType] = useState<"internal" | "client">("internal");
  const [modalStagedFiles, setModalStagedFiles] = useState<string[]>([]);
  const [attachmentsUploadingCount, setAttachmentsUploadingCount] = useState(0);

  // Preview modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<{ url: string; name: string }[]>([]);
  const [previewActiveIndex, setPreviewActiveIndex] = useState(0);

  const openUploadModal = (type: "internal" | "client") => {
    setUploadTargetType(type);
    setModalStagedFiles([]);
    setUploadModalOpen(true);
  };

  const handleUploadSuccess = (key: string, fileName: string) => {
    setModalStagedFiles((prev) => [...prev, key]);
    setAttachmentsUploadingCount((prev) => Math.max(0, prev - 1));
  };

  const handleConfirmModalAttachments = () => {
    if (uploadTargetType === "internal") {
      setInternalAttachments((prev) => [...prev, ...modalStagedFiles]);
    } else {
      setClientAttachments((prev) => [...prev, ...modalStagedFiles]);
    }
    setModalStagedFiles([]);
    setUploadModalOpen(false);
  };

  const removeAttachment = (index: number, type: "internal" | "client") => {
    if (type === "internal") {
      setInternalAttachments((prev) => prev.filter((_, i) => i !== index));
    } else {
      setClientAttachments((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handlePreviewFile = (urls: string[], index: number) => {
    setPreviewFiles(
      urls.map((url) => ({
        url,
        name: getFileNameFromUrl(url),
      })),
    );
    setPreviewActiveIndex(index);
    setIsPreviewOpen(true);
  };

  if (!data) return null;

  // ── READ-ONLY mode (no quoteId provided) ─────────────────────────────────────
  if (!quoteId) {
    return (
      <>
        <div className="d-flex flex-column gap-4">
          {!isCustomerLoggedIn() && (
            <div>
              <h4 className="fs-5 fw-bold text-black mb-md-3 mb-2">
                Internal Notes
              </h4>
              <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
                {internalNotes.length > 0 ? (
                  internalNotes.map((note) => (
                    <ReadOnlyNoteRow
                      key={note._id}
                      note={note}
                      onPreviewFile={handlePreviewFile}
                    />
                  ))
                ) : (
                  <div className="text-center text-muted py-3">
                    No internal notes added.
                  </div>
                )}
              </div>
            </div>
          )}
          <div>
            <h4 className="fs-5 fw-bold text-black mb-md-3 mb-2">Client Notes</h4>
            <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
              {clientNotes.length > 0 ? (
                clientNotes.map((note) => (
                  <ReadOnlyNoteRow
                    key={note._id}
                    note={note}
                    onPreviewFile={handlePreviewFile}
                  />
                ))
              ) : (
                <div className="text-center text-muted py-3">
                  No client notes added.
                </div>
              )}
            </div>
          </div>
        </div>
        <CommonFilePreviewModal
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          files={previewFiles}
          activeIndex={previewActiveIndex}
          onActiveIndexChange={(idx) => setPreviewActiveIndex(idx)}
        />
      </>
    );
  }

  // ── EDITABLE mode helpers (quoteId provided) ──────────────────────────────────

  const handleAdd = (type: "internal" | "client") => {
    if (type === "internal") {
      setInternalText("");
      setInternalAttachments([]);
      setIsAddingInternal(true);
      setEditingInternalId(null);
    } else {
      setClientText("");
      setClientAttachments([]);
      setIsAddingClient(true);
      setEditingClientId(null);
    }
  };

  const handleEdit = (note: NoteItem, type: "internal" | "client") => {
    if (type === "internal") {
      setInternalText(note.content);
      setInternalAttachments(note.attachments || []);
      setEditingInternalId(note._id);
      setIsAddingInternal(false);
    } else {
      setClientText(note.content);
      setClientAttachments(note.attachments || []);
      setEditingClientId(note._id);
      setIsAddingClient(false);
    }
  };

  const cancelAction = (type: "internal" | "client") => {
    if (type === "internal") {
      setIsAddingInternal(false);
      setEditingInternalId(null);
      setInternalText("");
      setInternalAttachments([]);
    } else {
      setIsAddingClient(false);
      setEditingClientId(null);
      setClientText("");
      setClientAttachments([]);
    }
  };

  const saveNote = async (type: "internal" | "client") => {
    const currentText = type === "internal" ? internalText : clientText;
    const currentAttachments =
      type === "internal" ? internalAttachments : clientAttachments;
    if (!currentText.trim()) return;

    const isEditing = type === "internal" ? editingInternalId : editingClientId;
    const setSaving = type === "internal" ? setSavingInternal : setSavingClient;

    setSaving(true);
    const payload = {
      content: currentText.trim(),
      type,
      attachments: currentAttachments,
    };

    if (isEditing) {
      await dispatch(
        updateQuotationNoteAPI({
          quote_id: quoteId,
          note_id: isEditing,
          payload,
          onSuccess: (updatedData: any) => {
            const rawNote = updatedData?.note || updatedData;
            const updated: NoteItem = {
              ...rawNote,
              attachments: (rawNote.attachments || [])
                .map((a: any) => (typeof a === "string" ? a : a?.file_url || ""))
                .filter(Boolean),
            };
            if (type === "internal") {
              setInternalNotes((prev) =>
                prev.map((n) => (n._id === isEditing ? updated : n)),
              );
            } else {
              setClientNotes((prev) =>
                prev.map((n) => (n._id === isEditing ? updated : n)),
              );
            }
            setSaving(false);
            cancelAction(type);
            onNotesChange?.({ internal: internalNotes, client: clientNotes });
          },
          onError: () => setSaving(false),
        }),
      );
    } else {
      dispatch(
        createQuotationNoteAPI({
          quote_id: quoteId,
          payload,
          onSuccess: (resData: any) => {
            const rawNote = resData?.note || resData;
            const newNote: NoteItem = {
              ...rawNote,
              attachments: (rawNote.attachments || [])
                .map((a: any) => (typeof a === "string" ? a : a?.file_url || ""))
                .filter(Boolean),
            };
            if (type === "internal") {
              setInternalNotes((prev) => [...prev, newNote]);
            } else {
              setClientNotes((prev) => [...prev, newNote]);
            }
            setSaving(false);
            cancelAction(type);
            onNotesChange?.({ internal: internalNotes, client: clientNotes });
          },
          onError: () => setSaving(false),
        }),
      );
    }
  };

  const deleteNote = (id: string, type: "internal" | "client") => {
    dispatch(
      deleteQuotationNoteAPI({
        quote_id: quoteId,
        note_id: id,
        type,
        onSuccess: () => {
          if (type === "internal")
            setInternalNotes((prev) => prev.filter((n) => n._id !== id));
          else setClientNotes((prev) => prev.filter((n) => n._id !== id));
          onNotesChange?.({ internal: internalNotes, client: clientNotes });
        },
        onError: () => {},
      }),
    );
  };

  const handleDeleteConfirm = () => {
    if (noteIdToDelete && noteTypeToDelete) {
      deleteNote(noteIdToDelete, noteTypeToDelete);
      setNoteIdToDelete(null);
      setNoteTypeToDelete(null);
    }
  };

  const renderAddRow = (type: "internal" | "client") => {
    const text = type === "internal" ? internalText : clientText;
    const setText = type === "internal" ? setInternalText : setClientText;
    const saving = type === "internal" ? savingInternal : savingClient;
    const attachments =
      type === "internal" ? internalAttachments : clientAttachments;

    return (
      <div className="card border-light-subtle rounded-3 shadow-none mt-2">
        <div className="card-body py-2 px-md-3 px-2">
          <div className="d-flex align-items-center gap-3">
            <Input.TextArea
              variant="borderless"
              placeholder="Add Notes"
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              className="p-0 fs-6"
              autoSize={{ minRows: 2, maxRows: 6 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  saveNote(type);
                }
              }}
            />
            <div className="d-flex gap-2 flex-shrink-0">
              <Tooltip title="Save">
                <Button
                  onClick={() => saveNote(type)}
                  icon={svg?.checkButton}
                  className="border-0 svg-32 p-0"
                  loading={saving}
                />
              </Tooltip>
              <Tooltip title="Cancel">
                <Button
                  onClick={() => cancelAction(type)}
                  icon={svg?.crossButton}
                  className="border-0 svg-32 p-0"
                />
              </Tooltip>
            </div>
          </div>

          <div className="mt-2">
            <Button
              type="text"
              className="fw-bold p-0 h-auto fs-6 text-decoration-underline text-primary d-inline-flex align-items-center gap-1"
              onClick={() => openUploadModal(type)}
            >
              <PaperClipOutlined /> Add Attachment
            </Button>
          </div>

          {renderAttachmentTags(
            attachments,
            true,
            (idx) => removeAttachment(idx, type),
            handlePreviewFile
          )}
        </div>
      </div>
    );
  };

  // ── EDITABLE render ───────────────────────────────────────────────────────────
  return (
    <>
      <div className="d-flex flex-column gap-4">
        {/* Internal Notes */}
        {!isCustomerLoggedIn() && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-md-3 mb-2">
              <h4 className="fs-5 fw-bold text-black mb-0">Internal Notes</h4>
              <Button
                type="text"
                className="fw-bold p-0 h-auto fs-6 text-decoration-underline"
                onClick={() => handleAdd("internal")}
              >
                + Add
              </Button>
            </div>
            <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
              {internalNotes.length === 0 && !isAddingInternal ? (
                <div className="text-center text-muted py-3">
                  No internal notes added.
                </div>
              ) : (
                internalNotes.map((note) => (
                  <NoteRow
                    key={note._id}
                    note={note}
                    type="internal"
                    editingId={editingInternalId}
                    currentText={internalText}
                    setText={setInternalText}
                    currentAttachments={internalAttachments}
                    openUploadModal={openUploadModal}
                    removeAttachment={removeAttachment}
                    saveNote={saveNote}
                    cancelAction={cancelAction}
                    handleEdit={handleEdit}
                    onDeleteClick={(id, type) => {
                      setNoteIdToDelete(id);
                      setNoteTypeToDelete(type);
                    }}
                    savingState={savingInternal}
                    onPreviewFile={handlePreviewFile}
                  />
                ))
              )}
              {isAddingInternal && renderAddRow("internal")}
            </div>
          </div>
        )}

        {/* Client Notes */}
        <div>
          <div className="d-flex justify-content-between align-items-center mb-md-3 mb-2">
            <h4 className="fs-5 fw-bold text-black mb-0">Client Notes</h4>
            {!isCustomerLoggedIn() ? (
              <Button
                type="text"
                className="fw-bold p-0 h-auto fs-6 text-decoration-underline"
                onClick={() => handleAdd("client")}
              >
                + Add
              </Button>
            ) : (
              ""
            )}
          </div>
          <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white">
            {clientNotes.length === 0 && !isAddingClient ? (
              <div className="text-center text-muted py-3">
                No client notes added.
              </div>
            ) : (
              clientNotes.map((note) => (
                <NoteRow
                  key={note._id}
                  note={note}
                  type="client"
                  editingId={editingClientId}
                  currentText={clientText}
                  setText={setClientText}
                  currentAttachments={clientAttachments}
                  openUploadModal={openUploadModal}
                  removeAttachment={removeAttachment}
                  saveNote={saveNote}
                  cancelAction={cancelAction}
                  handleEdit={handleEdit}
                  onDeleteClick={(id, type) => {
                    setNoteIdToDelete(id);
                    setNoteTypeToDelete(type);
                  }}
                  savingState={savingClient}
                  onPreviewFile={handlePreviewFile}
                />
              ))
            )}
            {isAddingClient && renderAddRow("client")}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <Modal
        title={
          <div className="text-center fs-4 fw-bolder lh-m">
            Upload Attachments
          </div>
        }
        open={uploadModalOpen}
        centered
        onCancel={() => {
          setModalStagedFiles([]);
          setUploadModalOpen(false);
        }}
        width={700}
        closeIcon={svg.closeIcon}
        footer={
          <div className="d-flex justify-content-center gap-2">
            <Button
              className="btn btn-lg btn-outline-secondary min-w-130 h-auto text-uppercase"
              onClick={() => {
                setModalStagedFiles([]);
                setUploadModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="btn btn-lg btn-primary min-w-130 h-auto text-uppercase"
              onClick={handleConfirmModalAttachments}
              disabled={attachmentsUploadingCount > 0}
            >
              Continue
            </Button>
          </div>
        }
        className="commonModalClass"
      >
        <div className="p-3">
          <S3FileUploader
            folder="quotation_note_attachments"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp,.svg,.mp4,.mov,.avi,.mkv,.zip,.rar"
            multiple={true}
            maxSize={500 * 1024 * 1024}
            onUploadStart={() =>
              setAttachmentsUploadingCount((prev) => prev + 1)
            }
            onSuccess={handleUploadSuccess}
            onError={() =>
              setAttachmentsUploadingCount((prev) => Math.max(0, prev - 1))
            }
            className="bg-white rounded-3 py-4"
            style={{ border: "1.5px dashed #999" }}
          >
            {attachmentsUploadingCount > 0 ? (
              <div className="py-2 text-center">
                <Spin tip="Uploading Attachment..." />
              </div>
            ) : (
              <>
                <p className="ant-upload-drag-icon text-center mb-2 d-flex justify-content-center">
                  <span className="svg-48">{svg?.importIcon || "📄"}</span>
                </p>
                <p className="ant-upload-text fw-semibold text-center fs-6 text-secondary">
                  Drag & Drop file to upload /{" "}
                  <span className="text-primary text-decoration-underline cursor-pointer">
                    CLICK HERE
                  </span>
                </p>
              </>
            )}
          </S3FileUploader>

          {modalStagedFiles.length > 0 && (
            <div
              className="d-flex flex-column gap-2 mt-3"
              style={{ maxHeight: 250, overflowY: "auto" }}
            >
              <div className="fw-semibold fs-7 text-secondary">
                Uploaded Files ({modalStagedFiles.length}):
              </div>
              {modalStagedFiles.map((url, idx) => (
                <div
                  key={idx}
                  className="border px-3 py-2 rounded-2 d-flex align-items-center justify-content-between bg-light gap-2"
                >
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ minWidth: 0, flexGrow: 1 }}
                  >
                    <div className="flex-shrink-0">
                      {getFileIcon(getFileNameFromUrl(url))}
                    </div>
                    <div
                      className="fw-semibold text-dark text-break fs-7"
                      style={{ minWidth: 0, flexGrow: 1 }}
                    >
                      {getFileNameFromUrl(url)}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <Button
                      type="text"
                      danger
                      icon={svg?.deleteBtn || <DeleteOutlined />}
                      className="border-0 svg-32 p-0 d-flex align-items-center justify-content-center"
                      onClick={() => {
                        const fileUrl = url;
                        setModalStagedFiles((prev) =>
                          prev.filter((_, i) => i !== idx),
                        );
                        if (fileUrl) {
                          deleteFromS3(fileUrl).catch(() => {});
                        }
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Common File Preview Modal */}
      <CommonFilePreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        files={previewFiles}
        activeIndex={previewActiveIndex}
        onActiveIndexChange={(idx) => setPreviewActiveIndex(idx)}
      />

      {/* Delete confirmation modal */}
      <CommonConfirmationModal
        open={noteIdToDelete != null}
        title="Remove Note, Are You Sure?"
        subTitle="Removing this Note will delete it from the quotation. Please confirm before proceeding."
        onSuccess={handleDeleteConfirm}
        onClose={() => {
          setNoteIdToDelete(null);
          setNoteTypeToDelete(null);
        }}
      />
    </>
  );
};

export default NotesDetails;
